import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { qk } from './queryKeys';
import type {
  AdminMembershipRowDto, AdminMembershipsDto, AdminMembershipUpdateRequest, Guid, MembershipAudience, MembershipCheckoutDto, MembershipPageDto,
  MembershipPlanDto, MembershipSettingsDto, MembershipStatusDto, StartMembershipRequest, UpsertMembershipPlanRequest,
} from './types';

/** Whose membership: an organization's (its owners and admins manage it) or the signed-in tester's own. */
export type MembershipSubject = { kind: 'org'; id: Guid } | { kind: 'me' };

const base = (s: MembershipSubject) => (s.kind === 'org' ? `/api/organizations/${s.id}/membership` : '/api/me/membership');
const key = (s: MembershipSubject) => ['membership', s.kind, s.kind === 'org' ? s.id : 'me'] as const;

export const membershipsApi = {
  page: (s: MembershipSubject) => api<MembershipPageDto>(base(s)),
  start: (s: MembershipSubject, body: StartMembershipRequest) => api<MembershipCheckoutDto>(`${base(s)}/checkout`, { method: 'POST', body }),
  cancel: (s: MembershipSubject) => api<MembershipStatusDto>(`${base(s)}/cancel`, { method: 'POST' }),
  change: (s: MembershipSubject, planId: Guid) => api<MembershipStatusDto>(`${base(s)}/change`, { method: 'POST', body: { planId } }),
  admin: () => api<AdminMembershipsDto>('/api/admin/memberships'),
  saveSettings: (body: MembershipSettingsDto) => api<MembershipSettingsDto>('/api/admin/memberships/settings', { method: 'PUT', body }),
  createPlan: (body: UpsertMembershipPlanRequest) => api<MembershipPlanDto>('/api/admin/memberships/plans', { method: 'POST', body }),
  updatePlan: (id: Guid, body: UpsertMembershipPlanRequest) => api<MembershipPlanDto>(`/api/admin/memberships/plans/${id}`, { method: 'PUT', body }),
  updateSubject: (audience: MembershipAudience, id: Guid, body: AdminMembershipUpdateRequest) =>
    api<AdminMembershipRowDto>(`/api/admin/memberships/subjects/${audience}/${id}`, { method: 'PUT', body }),
  setSimulator: (failRenewals: boolean) => api<AdminMembershipsDto>('/api/admin/memberships/simulator', { method: 'PUT', body: { failRenewals } }),
  renewNow: (membershipId: Guid) => api<AdminMembershipsDto>(`/api/admin/memberships/simulator/${membershipId}/renew`, { method: 'POST' }),
};

export function useMembership(subject: MembershipSubject | null) {
  return useQuery({
    queryKey: subject ? key(subject) : ['membership', 'none'],
    queryFn: () => membershipsApi.page(subject!),
    enabled: !!subject,
  });
}

/** A membership change also changes what the profile says (banner, gates). */
function useMembershipMutation<A, R>(fn: (arg: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['membership'] });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export const useStartMembership = (s: MembershipSubject) => useMembershipMutation((body: StartMembershipRequest) => membershipsApi.start(s, body));
export const useCancelMembership = (s: MembershipSubject) => useMembershipMutation(() => membershipsApi.cancel(s));
export const useChangeMembershipPlan = (s: MembershipSubject) => useMembershipMutation((planId: Guid) => membershipsApi.change(s, planId));

export function useAdminMemberships(enabled = true) {
  return useQuery({ queryKey: ['membership', 'admin'], queryFn: membershipsApi.admin, enabled });
}

export const useSaveMembershipSettings = () => useMembershipMutation(membershipsApi.saveSettings);
export const useCreateMembershipPlan = () => useMembershipMutation(membershipsApi.createPlan);
export const useUpdateMembershipPlan = () =>
  useMembershipMutation(({ id, body }: { id: Guid; body: UpsertMembershipPlanRequest }) => membershipsApi.updatePlan(id, body));
export const useUpdateMembershipSubject = () =>
  useMembershipMutation(({ audience, id, body }: { audience: MembershipAudience; id: Guid; body: AdminMembershipUpdateRequest }) =>
    membershipsApi.updateSubject(audience, id, body));
export const useMembershipSimulator = () => useMembershipMutation(membershipsApi.setSimulator);
export const useRenewMembershipNow = () => useMembershipMutation(membershipsApi.renewNow);
